import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shorten a full name so that the last word becomes an initial.
 * "Recep Sengül"       → "Recep S."
 * "Ahmet Mehmet Yılmaz"→ "Ahmet Mehmet Y."
 * "Madonna"            → "Madonna"
 * Türkçe karakter uyumlu: toLocaleUpperCase('tr')
 */
export function shortenName(fullName) {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  const firsts = parts.slice(0, -1).join(' ');
  return `${firsts} ${last.charAt(0).toLocaleUpperCase('tr')}.`;
}

async function fetchLeaderboard(period) {
  const token = getToken();
  const res = await axios.get(`${API}/cabin/leaderboard?period=${period}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const raw = res.data;

  // Normalise: backend may return { rows, current_user_rank, period } or
  // legacy { entries, your_rank, ... } or a plain array.
  let entries;
  let yourRank = null;
  let yourXp = null;
  let yourMiles = null;

  if (Array.isArray(raw)) {
    entries = raw;
  } else if (Array.isArray(raw.rows)) {
    // Current backend shape — current_user_rank is an OBJECT
    // {rank, user_id, name, total_xp, total_miles, ...}
    entries = raw.rows;
    const cur = raw.current_user_rank;
    if (cur && typeof cur === 'object') {
      yourRank = cur.rank ?? null;
      yourXp = cur.total_xp ?? null;
      yourMiles = cur.total_miles ?? null;
    } else {
      yourRank = cur ?? null;  // backward compat if numeric
    }
  } else if (Array.isArray(raw.entries)) {
    // Legacy / already-mapped shape
    entries = raw.entries;
    yourRank = raw.your_rank ?? null;
    yourXp = raw.your_xp ?? null;
    yourMiles = raw.your_miles ?? null;
  } else {
    entries = [];
  }

  // Derive yourXp / yourMiles from the current user's row when not provided
  if ((yourXp == null || yourMiles == null) && yourRank != null) {
    // Try to find current user row by rank (best-effort; user_id match done in component)
    const currentRow = entries.find((e) => e.rank === yourRank);
    if (currentRow) {
      if (yourXp == null) yourXp = currentRow.total_xp ?? null;
      if (yourMiles == null) yourMiles = currentRow.total_miles ?? null;
    }
  }

  // Add displayName (shortened) to every entry; keep original name intact
  const mappedEntries = entries.map((e) => ({
    ...e,
    displayName: shortenName(e.name),
  }));

  return {
    period,
    entries: mappedEntries,
    your_rank: yourRank,
    your_xp: yourXp,
    your_miles: yourMiles,
    total: mappedEntries.length,
  };
}

export function useLeaderboard(period = 'weekly') {
  return useQuery({
    queryKey: ['cabin', 'leaderboard', period],
    queryFn: () => fetchLeaderboard(period),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}
